import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, MessageCircle, Send, Bot, User, Check, Loader2, Plus, Smile, RotateCcw } from 'lucide-react';
import CallbackModal from './CallbackModal';
import destinationsData from '../data/destinations.json';
import './Hero.css';

const Hero = () => {
  const navigate = useNavigate();
  
  const [activities, setActivities] = useState([
    {
      id: 1,
      type: 'Accommodation',
      title: 'Singapore : 5-Star Luxury Hotel Stay',
      duration: '2 Nights',
      day: 'Day 1-2',
      image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=300&h=200&fit=crop',
      status: 'added',
      location: 'Singapore'
    }
  ]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentInput, setCurrentInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showLeftScroll, setShowLeftScroll] = useState(false);
  const [showRightScroll, setShowRightScroll] = useState(true);
  const [destinationScrollStates, setDestinationScrollStates] = useState(() => {
    const initialState = {};
    destinationsData.defaultDestinations.forEach(destination => {
      initialState[destination] = { showLeft: false, showRight: true };
    });
    return initialState;
  });

  const [imageScrollStates, setImageScrollStates] = useState({});
  const [isNavFixed, setIsNavFixed] = useState(false);
  const [selectedDestination, setSelectedDestination] = useState('All Destinations');
  const [isCallbackModalOpen, setIsCallbackModalOpen] = useState(false);

  const activitiesContainerRef = useRef(null);
  const navContainerRef = useRef(null);
  const destinationRefs = useRef({});
  const imageRefs = useRef({});
  const navOriginalTopRef = useRef(null);

  const scrollToBottom = () => {
    if (activitiesContainerRef.current) {
      activitiesContainerRef.current.scrollTop = activitiesContainerRef.current.scrollHeight;
    }
  };

  const scrollNavRight = () => {
    if (navContainerRef.current) {
      navContainerRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  const scrollNavLeft = () => {
    if (navContainerRef.current) {
      navContainerRef.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };

  const updateScrollButtons = () => {
    if (navContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = navContainerRef.current;
      const isAtStart = scrollLeft <= 0;
      const isAtEnd = scrollLeft >= scrollWidth - clientWidth - 1;
      
      setShowLeftScroll(!isAtStart);
      setShowRightScroll(!isAtEnd);
    }
  };

  // Function to scroll to a specific destination section below
  const scrollToDestinationSection = (destinationName) => {
    // Special case for 'Explore' - scroll to top of destinations
    if (destinationName === 'Explore') {
      setSelectedDestination('All Destinations');
      // Scroll to the destination sections with offset for nav
      setTimeout(() => {
        const destinationSections = document.querySelector('.destination-sections');
        const destinationNav = document.querySelector('.destination-nav');
        if (destinationSections && destinationNav) {
          const navHeight = destinationNav.offsetHeight;
          const elementPosition = destinationSections.getBoundingClientRect().top + window.pageYOffset;
          const offsetPosition = elementPosition - navHeight - 20; // Extra 20px padding

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
      return;
    }
    
    setSelectedDestination(destinationName);
    
    // Scroll to the specific destination section with offset for nav
    setTimeout(() => {
      if (destinationRefs.current[destinationName]) {
        const destinationNav = document.querySelector('.destination-nav');
        if (destinationNav) {
          const navHeight = destinationNav.offsetHeight;
          const elementPosition = destinationRefs.current[destinationName].getBoundingClientRect().top + window.pageYOffset;
          const offsetPosition = elementPosition - navHeight - 20; // Extra 20px padding

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }
    }, 100);
  };

  // Handle destination click
  const handleDestinationClick = (destinationName) => {
    scrollToDestinationSection(destinationName);
  };

  // Handle View All click
  const handleViewAll = (destinationKey) => {
    navigate(`/destination/${destinationKey}`);
  };

  // Handle Call button click
  const handleCallClick = () => {
    window.location.href = 'tel:+917447633655';
  };

  // Handle Callback button click
  const handleCallbackClick = () => {
    setIsCallbackModalOpen(true);
  };

  const scrollDestinationRight = (destination) => {
    const container = destinationRefs.current[destination];
    if (container) {
      container.scrollBy({ left: 320 + 24, behavior: 'smooth' }); // 320px card + 24px gap (1.5rem)
    }
  };

  const scrollDestinationLeft = (destination) => {
    const container = destinationRefs.current[destination];
    if (container) {
      container.scrollBy({ left: -(320 + 24), behavior: 'smooth' }); // 320px card + 24px gap (1.5rem)
    }
  };

  const updateDestinationScrollButtons = (destination) => {
    const container = destinationRefs.current[destination];
    if (container) {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      const isAtStart = scrollLeft <= 0;
      const isAtEnd = scrollLeft >= scrollWidth - clientWidth - 1;
      
      setDestinationScrollStates(prev => ({
        ...prev,
        [destination]: {
          showLeft: !isAtStart,
          showRight: !isAtEnd
        }
      }));
    }
  };

  const scrollImageRight = (imageId) => {
    const container = imageRefs.current[imageId];
    if (container) {
      container.scrollBy({ left: container.clientWidth, behavior: 'smooth' });
    }
  };

  const scrollImageLeft = (imageId) => {
    const container = imageRefs.current[imageId];
    if (container) {
      container.scrollBy({ left: -container.clientWidth, behavior: 'smooth' });
    }
  };

  const updateImageScrollButtons = (imageId) => {
    const container = imageRefs.current[imageId];
    if (container) {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      const isAtStart = scrollLeft <= 0;
      const isAtEnd = scrollLeft >= scrollWidth - clientWidth - 1;
      
      setImageScrollStates(prev => ({
        ...prev,
        [imageId]: {
          showLeft: !isAtStart,
          showRight: !isAtEnd
        }
      }));
    }
  };

  // Mouse dragging functionality for navigation
  useEffect(() => {
    const navContainer = navContainerRef.current;
    if (!navContainer) return;

    let isDown = false;
    let startX;
    let scrollLeft;

    const handleMouseDown = (e) => {
      isDown = true;
      navContainer.style.cursor = 'grabbing';
      startX = e.pageX - navContainer.offsetLeft;
      scrollLeft = navContainer.scrollLeft;
    };

    const handleMouseLeave = () => {
      isDown = false;
      navContainer.style.cursor = 'grab';
    };

    const handleMouseUp = () => {
      isDown = false;
      navContainer.style.cursor = 'grab';
    };

    const handleMouseMove = (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - navContainer.offsetLeft;
      const walk = (x - startX) * 2;
      navContainer.scrollLeft = scrollLeft - walk;
    };

    const handleScroll = () => {
      updateScrollButtons();
    };

    navContainer.addEventListener('mousedown', handleMouseDown);
    navContainer.addEventListener('mouseleave', handleMouseLeave);
    navContainer.addEventListener('mouseup', handleMouseUp);
    navContainer.addEventListener('mousemove', handleMouseMove);
    navContainer.addEventListener('scroll', handleScroll);

    return () => {
      navContainer.removeEventListener('mousedown', handleMouseDown);
      navContainer.removeEventListener('mouseleave', handleMouseLeave);
      navContainer.removeEventListener('mouseup', handleMouseUp);
      navContainer.removeEventListener('mousemove', handleMouseMove);
      navContainer.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [activities, isProcessing]);

  // Initialize destination scroll buttons on mount
  useEffect(() => {
    const initializeScrollButtons = () => {
      Object.keys(destinationRefs.current).forEach(destination => {
        if (destinationRefs.current[destination]) {
          updateDestinationScrollButtons(destination);
        }
      });
      
      Object.keys(imageRefs.current).forEach(imageId => {
        if (imageRefs.current[imageId]) {
          updateImageScrollButtons(imageId);
        }
      });
    };

    // Run after a short delay to ensure DOM is ready
    const timeoutId = setTimeout(initializeScrollButtons, 100);
    
    return () => clearTimeout(timeoutId);
  }, []); // Run once on mount

  // Store the original position of destination-nav on mount
  useEffect(() => {
    const destinationNav = document.querySelector('.destination-nav');
    if (destinationNav && !navOriginalTopRef.current) {
      const rect = destinationNav.getBoundingClientRect();
      navOriginalTopRef.current = rect.top + window.scrollY;
    }
  }, []);

  // Detect scroll to make nav-container fixed at top
  useEffect(() => {
    const handleScroll = () => {
      if (navOriginalTopRef.current) {
        const scrollPosition = window.scrollY;
        // Fixed when we scroll past the original position
        // Unfixed when we scroll back above the original position
        const shouldBeFixed = scrollPosition >= navOriginalTopRef.current;
        setIsNavFixed(shouldBeFixed);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check on mount
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const planCuratorData = [
    {
      type: 'Activity',
      title: 'Singapore : Gardens by the Bay',
      duration: '3 Hours',
      day: 'Day 1',
      image: 'https://images.unsplash.com/photo-1525625293386-3f8f99389edd?w=300&h=200&fit=crop',
      location: 'Singapore',
      input: 'Visit the iconic Gardens by the Bay with Supertree Grove...'
    },
    {
      type: 'Activity',
      title: 'Singapore : Night Safari Experience',
      duration: '4 Hours',
      day: 'Day 1',
      image: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=300&h=200&fit=crop',
      location: 'Singapore',
      input: 'Explore the world\'s first nocturnal wildlife park...'
    },
    {
      type: 'Activity',
      title: 'Singapore : Marina Bay Sands SkyPark',
      duration: '2 Hours',
      day: 'Day 2',
      image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=300&h=200&fit=crop',
      location: 'Singapore',
      input: 'Sky-high views and infinity pool experience...'
    },
    {
      type: 'Activity',
      title: 'Singapore : Sentosa Island Adventure',
      duration: '6 Hours',
      day: 'Day 2',
      image: 'https://images.unsplash.com/photo-1552465011-b4e21bf6e79a?w=300&h=200&fit=crop',
      location: 'Singapore',
      input: 'Full day at Sentosa with Universal Studios and beaches...'
    },
    {
      type: 'Activity',
      title: 'Singapore : Chinatown Food Tour',
      duration: '2 Hours',
      day: 'Day 2',
      image: 'https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=300&h=200&fit=crop',
      location: 'Singapore',
      input: 'Authentic local food experience in Chinatown...'
    }
  ];

  useEffect(() => {
    let timeoutId;
    let activityIndex = 0;

    const addNextActivity = () => {
      if (activityIndex < planCuratorData.length) {
        const nextActivity = planCuratorData[activityIndex];
        
        // Start typing the input
        setIsTyping(true);
        setCurrentInput('');
        
        let currentIndex = 0;
        const typingInterval = setInterval(() => {
          if (currentIndex < nextActivity.input.length) {
            setCurrentInput(nextActivity.input.slice(0, currentIndex + 1));
            currentIndex++;
          } else {
            clearInterval(typingInterval);
            setIsTyping(false);
            
            // Start processing
            setTimeout(() => {
              setIsProcessing(true);
              
              // Add activity with "adding" status
              const newActivity = {
                id: Date.now(),
                ...nextActivity,
                status: 'adding'
              };
              setActivities(prev => [...prev, newActivity]);
              
              // Simulate processing time
              setTimeout(() => {
                setIsProcessing(false);
                
                // Update activity to "added" status
                setActivities(prev => prev.map(activity => 
                  activity.id === newActivity.id 
                    ? { ...activity, status: 'added' }
                    : activity
                ));
                
                activityIndex++;
                
                // Continue to next activity or restart
                if (activityIndex < planCuratorData.length) {
                  timeoutId = setTimeout(addNextActivity, 3000);
                } else {
                  // Restart after all activities are added
                  timeoutId = setTimeout(() => {
                    setActivities([{
                      id: 1,
                      type: 'Accommodation',
                      title: 'Singapore : 5-Star Luxury Hotel Stay',
                      duration: '2 Nights',
                      day: 'Day 1-2',
                      image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=300&h=200&fit=crop',
                      status: 'added',
                      location: 'Singapore'
                    }]);
                    activityIndex = 0;
                    addNextActivity();
                  }, 5000);
                }
              }, 2000);
            }, 1000);
          }
        }, 50);
      }
    };

    // Start the plan curator after initial delay
    timeoutId = setTimeout(addNextActivity, 2000);
    
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []); // Empty dependency array to run only once

  return (
    <section className="hero">
      <div className="hero-background">
        <div className="hero-overlay"></div>
      </div>
      
      <div className="container">
        {/* Hero Title and Subtitle - Centered at Top */}
        <div className="hero-header">
          <h1 className="hero-title">
            Personalised Tours, Perfectly Crafted
            <span className="highlight"> with Emrys.</span>
          </h1>
        </div>

        {/* Plan Curator Window */}
        <div className="hero-chat-section">
          <div className="plan-curator-window">
            <div className="curator-header">
              <div className="curator-title">
                <MessageCircle size={20} />
                <span>Emrys Plan Curator</span>
              </div>
              <div className="curator-status">
                <div className="status-dot"></div>
                <span>Planning</span>
              </div>
            </div>
            
            <div className="activities-container" ref={activitiesContainerRef}>
              {activities.map((activity) => (
                <div key={activity.id} className="activity-card">
                  <div className="activity-image">
                    <img src={activity.image} alt={activity.title} />
                  </div>
                  <div className="activity-content">
                    <div className="activity-header">
                      <div className="activity-type">{activity.type}</div>
                      <div className="activity-day">{activity.day}</div>
                    </div>
                    <div className="activity-title">{activity.title}</div>
                    <div className="activity-duration">{activity.duration}</div>
                  </div>
                  <div className={`activity-status ${activity.status}`}>
                    {activity.status === 'added' ? (
                      <>
                        <Check size={16} />
                        <span>ADDED</span>
                      </>
                    ) : (
                      <>
                        <Loader2 size={16} className="spinning" />
                        <span>Adding...</span>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            {/* Fixed Input Card at Bottom */}
            <div className="input-card">
              <div className="input-content">
                <input 
                  type="text" 
                  value={currentInput}
                  placeholder="Type your travel request..."
                  disabled={true}
                  className={isTyping ? 'typing-active' : ''}
                />
              </div>
              <div className="input-actions">
                <div className="action-buttons">
                  <button className="action-btn">
                    <Plus size={16} />
                  </button>
                  <button className="action-btn">
                    <Smile size={16} />
                  </button>
                </div>
                <button className="retry-btn">
                  <RotateCcw size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>

                {/* Destination Navigation Bar */}
                <div className={`destination-nav ${isNavFixed ? 'fixed-header' : ''}`}>
                  <div className="nav-container" ref={navContainerRef}>
                    {showLeftScroll && (
                      <div className="nav-scroll-btn nav-scroll-left" onClick={scrollNavLeft}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                    )}
                    {destinationsData.navigationDestinations.map((navItem, index) => (
                      <div 
                        key={index} 
                        className={`nav-item ${navItem.isActive ? 'active' : ''} ${navItem.isTrending ? 'trending' : ''}`}
                        onClick={() => handleDestinationClick(navItem.name)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="nav-icon">{navItem.icon}</div>
                        <span>{navItem.name}</span>
                        {navItem.isTrending && <div className="trending-badge">Trending</div>}
                      </div>
                    ))}
                    {showRightScroll && (
                      <div className="nav-scroll-btn nav-scroll-right" onClick={scrollNavRight}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                    )}
                  </div>
                </div>

                {/* Spacer for fixed nav when it becomes fixed header */}
                {isNavFixed && <div style={{ height: '80px' }}></div>}

        {/* Destination Sections with Horizontal Scroll */}
        <div className="destination-sections">
          {Object.keys(destinationsData.destinations).map((destinationKey) => {
            const destination = destinationsData.destinations[destinationKey];
            return (
              <div key={destinationKey} className="destination-section" ref={(el) => destinationRefs.current[destinationKey] = el}>
                <div className="section-header">
                  <h2>{destination.name}</h2>
                  <div className="section-actions">
                    <button 
                      onClick={() => handleViewAll(destinationKey)}
                      className="view-all"
                      style={{ cursor: 'pointer', border: 'none', background: 'none', color: 'inherit', textDecoration: 'underline' }}
                    >
                      View All
                    </button>
                  </div>
                </div>
                <div className="packages-scroll">
                  {destinationScrollStates[destinationKey]?.showLeft && (
                    <button className="scroll-left-btn" onClick={() => scrollDestinationLeft(destinationKey)}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  )}
                  <div className="packages-container" ref={(el) => destinationRefs.current[destinationKey] = el} onScroll={() => updateDestinationScrollButtons(destinationKey)}>
                    {destination.packages.map((pkg, index) => (
                      <div key={pkg.id} className="package-card">
                        <div className="package-image">
                          <div className="image-scroll-container">
                            {imageScrollStates[pkg.id]?.showLeft && (
                              <button className="image-scroll-left" onClick={() => scrollImageLeft(pkg.id)}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                                  <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </button>
                            )}
                            <div className="images-container" ref={(el) => imageRefs.current[pkg.id] = el} onScroll={() => updateImageScrollButtons(pkg.id)}>
                              {pkg.images.map((imageUrl, imgIndex) => (
                                <img key={imgIndex} src={imageUrl} alt={`${pkg.title} - Image ${imgIndex + 1}`} />
                              ))}
                            </div>
                            {imageScrollStates[pkg.id]?.showRight && (
                              <button className="image-scroll-right" onClick={() => scrollImageRight(pkg.id)}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                                  <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </button>
                            )}
                          </div>
                          <div className="image-dots">
                            {pkg.images.map((_, dotIndex) => (
                              <span key={dotIndex} className={`dot ${dotIndex === 0 ? 'active' : ''}`}></span>
                            ))}
                          </div>
                        </div>
                        <div className="package-info">
                          <div className="package-duration-rating">
                            <span className="duration">{pkg.duration}</span>
                            <div className="rating">
                              <span className="star">★</span>
                              <span>{pkg.rating}</span>
                            </div>
                          </div>
                          <h3 className="package-title">{pkg.title}</h3>
                          <p className="package-itinerary">{pkg.itinerary}</p>
                          <div className="package-pricing">
                            <div className="price-row">
                              <span className="original-price">{pkg.originalPrice}</span>
                              <span className="savings">{pkg.savings}</span>
                            </div>
                            <div className="final-price">{pkg.finalPrice}/Adult</div>
                          </div>
                          <div className="package-actions">
                            <button className="call-btn" onClick={handleCallClick}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </button>
                            <button className="callback-btn" onClick={handleCallbackClick}>Request Callback</button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {destinationScrollStates[destinationKey]?.showRight && (
                    <button className="scroll-right-btn" onClick={() => scrollDestinationRight(destinationKey)}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Callback Modal */}
      <CallbackModal 
        isOpen={isCallbackModalOpen}
        onClose={() => setIsCallbackModalOpen(false)}
      />
    </section>
  );
};

export default Hero;
